// generateMSSPReport.js
// Browser-side PPTX generation for MSSP ACO Executive Intelligence Report
// Called from ACODeepDive when user clicks "Export Report"

const NAVY    = "0D1F2D";
const NAVY2   = "1A3A52";
const TEAL    = "42BA97";
const TEAL_D  = "2E9478";
const WHITE   = "FFFFFF";
const SLATE   = "94A3B8";
const SLATE_L = "CBD5E1";
const GOLD    = "DDAA66";
const GRAY_50 = "F8FAFC";
const RED     = "C53030";
const GREEN_D = "2E7D4F";

const makeShadow = () => ({ type:"outer", blur:8, offset:3, angle:135, color:"000000", opacity:0.12 });

function loadPptxGenJS() {
  return new Promise((resolve, reject) => {
    if (window.PptxGenJS) { resolve(window.PptxGenJS); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/pptxgenjs@3.12.0/dist/pptxgen.bundle.js';
    script.onload = () => resolve(window.PptxGenJS);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

// ── Data helpers ──────────────────────────────────────────────────────────────
function avg(arr, fn) {
  const vals = arr.map(fn).filter(v => v != null && !isNaN(v));
  return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
}

function peerAvg(peerRecs, key) {
  return avg(peerRecs, r => r[key]);
}

function peerPct(peerRecs, key, val) {
  if (val == null) return null;
  const vals = [...peerRecs.map(r => r[key]).filter(v => v != null), val].sort((a, b) => a - b);
  const rank = vals.filter(v => v < val).length;
  return Math.round(rank / Math.max(vals.length - 1, 1) * 100);
}

function fmtM(v) {
  if (v == null) return '—';
  if (Math.abs(v) >= 1e9) return `$${(v/1e9).toFixed(2)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v/1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v/1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

// sav_rate in MSSP is decimal (0.05 = 5%)
function fmtSav(v) {
  if (v == null) return '—';
  return `${(v * 100).toFixed(2)}%`;
}

function peerPctSav(peerRecs, val) {
  if (val == null) return null;
  const vals = [...peerRecs.map(r => r.sav_rate).filter(v => v != null), val].sort((a, b) => a - b);
  const rank = vals.filter(v => v < val).length;
  return Math.round(rank / Math.max(vals.length - 1, 1) * 100);
}

// Auto-generated key finding sentence
function keyFinding(aco, latest, peerRecs, acoRecords) {
  const savPct = peerPctSav(peerRecs, latest?.sav_rate);
  const snfPct = peerPct(peerRecs, 'p_snf_adm', latest?.p_snf_adm);
  const yearsEarning = acoRecords.filter(r => (r.earn_save_loss||0) > 0).length;
  const savRate = latest?.sav_rate;

  let finding = '';
  if (savPct != null && savPct >= 75) {
    finding = `${aco.aco_name} ranked in the ${savPct}th percentile for savings rate among same-track peers in PY${latest?.performance_year}`;
    if (snfPct != null && snfPct > 60) {
      finding += ` — with SNF utilization above ${snfPct - 50}% of peers, representing a key cost optimization opportunity.`;
    } else {
      finding += ` — among the strongest performers in the program.`;
    }
  } else if (savPct != null && savPct < 40) {
    finding = `${aco.aco_name} has room to grow: savings rate ranks in the ${savPct}th percentile vs peers`;
    if (snfPct != null && snfPct > 60) {
      finding += `. SNF utilization above peer average is the leading cost driver to address.`;
    } else {
      finding += `. ${yearsEarning} of ${acoRecords.length} years showed positive savings — the trajectory matters.`;
    }
  } else {
    finding = `${aco.aco_name} has earned savings in ${yearsEarning} of ${acoRecords.length} MSSP years`;
    finding += savRate != null ? `, with a PY${latest?.performance_year} rate of ${fmtSav(savRate)}.` : '.';
  }
  return finding;
}

// ── Main export function ───────────────────────────────────────────────────────
export async function generateMSSPReport(aco, acoRecords, perfData, onProgress) {
  onProgress?.('Loading report engine...');
  const PptxGenJS = await loadPptxGenJS();
  const pres = new PptxGenJS();
  pres.layout = 'LAYOUT_16x9';
  pres.title = `MSSP ACO Intelligence Report: ${aco.aco_name}`;

  const latest = acoRecords[acoRecords.length - 1];
  const prior  = acoRecords[acoRecords.length - 2];

  // Peer records: same track, same year, no mid-year terminations
  const peerRecs = (perfData || []).filter(r =>
    r.performance_year === latest?.performance_year &&
    r.current_track === latest?.current_track &&
    r.is_midyear === 0 &&
    r.aco_id !== latest?.aco_id
  );

  // All-time aggregates
  const totalEarned = acoRecords.reduce((s, r) => s + (r.earn_save_loss||0), 0);
  const yearsEarning = acoRecords.filter(r => (r.earn_save_loss||0) > 0).length;
  const avgSavRate = avg(acoRecords, r => r.sav_rate);

  const finding = keyFinding(aco, latest, peerRecs, acoRecords);

  onProgress?.('Building slides...');

  // ── SLIDE 1: Cover ─────────────────────────────────────────────────────────
  const s1 = pres.addSlide();
  s1.background = { color: NAVY };
  s1.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:0.08, h:5.625, fill:{ color:TEAL } });

  s1.addText("TRYNYTY", { x:0.35, y:0.28, w:3, h:0.38,
    fontSize:20, bold:true, color:WHITE, fontFace:"Calibri", charSpacing:3, margin:0 });
  s1.addText("health:enablement", { x:0.35, y:0.64, w:3, h:0.22,
    fontSize:10, color:TEAL, fontFace:"Calibri", margin:0 });

  // Data badge
  s1.addShape(pres.shapes.RECTANGLE, { x:7.0, y:0.28, w:2.7, h:0.42,
    fill:{ color:NAVY2 }, line:{ color:TEAL, width:0.5 } });
  s1.addText(`PY 2013–${latest?.performance_year||2024} · MSSP`, {
    x:7.0, y:0.28, w:2.7, h:0.42,
    fontSize:10, bold:true, color:TEAL, fontFace:"Calibri", align:"center", margin:0 });

  s1.addText("MSSP ACO", { x:0.35, y:1.2, w:9.3, h:0.55,
    fontSize:36, bold:true, color:WHITE, fontFace:"Calibri", margin:0 });
  s1.addText("Executive Intelligence Report", { x:0.35, y:1.72, w:9.3, h:0.48,
    fontSize:30, color:TEAL, fontFace:"Calibri", italic:true, margin:0 });

  // ACO name banner
  s1.addShape(pres.shapes.RECTANGLE, { x:0.35, y:2.38, w:9.3, h:0.78,
    fill:{ color:NAVY2 }, line:{ color:TEAL, width:1 } });
  s1.addText(aco.aco_name, { x:0.5, y:2.38, w:9.0, h:0.78,
    fontSize:22, bold:true, color:WHITE, fontFace:"Calibri", valign:"middle", margin:0 });

  // Track / years tags
  const tags = [
    latest?.current_track || '—',
    latest?.risk_model || '—',
    `${acoRecords.length} Year${acoRecords.length!==1?'s':''} in MSSP`,
    ...(latest?.snf_waiver ? ['SNF Waiver'] : []),
  ];
  let tx = 0.35;
  tags.forEach(tag => {
    const tw = Math.max(1.2, tag.length * 0.095 + 0.5);
    s1.addShape(pres.shapes.RECTANGLE, { x:tx, y:3.28, w:tw, h:0.32,
      fill:{ color:"0A1929" }, line:{ color:TEAL, width:0.5 } });
    s1.addText(tag, { x:tx+0.06, y:3.28, w:tw-0.12, h:0.32,
      fontSize:10, bold:true, color:TEAL, fontFace:"Calibri", valign:"middle", margin:0 });
    tx += tw + 0.18;
  });

  // Key finding sentence
  s1.addShape(pres.shapes.RECTANGLE, { x:0.35, y:3.75, w:9.3, h:0.72,
    fill:{ color:"0A1929" }, line:{ color:"1E3A5A", width:0.5 } });
  s1.addText("KEY FINDING", { x:0.55, y:3.8, w:2, h:0.2,
    fontSize:8, bold:true, color:GOLD, fontFace:"Calibri", charSpacing:1.5, margin:0 });
  s1.addText(finding, { x:0.55, y:4.0, w:9.0, h:0.38,
    fontSize:11, color:SLATE_L, fontFace:"Calibri", margin:0 });

  // Bottom stats bar
  s1.addShape(pres.shapes.RECTANGLE, { x:0.35, y:4.58, w:9.3, h:0.78,
    fill:{ color:"061020" }, line:{ color:"061020" } });
  [
    { v: fmtM(totalEarned), l:'Total Earned Savings' },
    { v: `${yearsEarning}/${acoRecords.length}`, l:'Years Earning Savings' },
    { v: fmtSav(avgSavRate), l:'Avg Savings Rate' },
    { v: fmtSav(latest?.sav_rate), l:`PY${latest?.performance_year} Savings Rate` },
    { v: latest?.qual_score?.toFixed(1)||'—', l:'Latest Quality Score' },
  ].forEach((s, i) => {
    const sx = 0.55 + i * 1.84;
    s1.addText(s.v, { x:sx, y:4.65, w:1.7, h:0.38,
      fontSize:17, bold:true, color:TEAL, fontFace:"Calibri", margin:0 });
    s1.addText(s.l, { x:sx, y:5.01, w:1.7, h:0.2,
      fontSize:8, color:SLATE, fontFace:"Calibri", margin:0 });
  });

  s1.addText(`Prepared by TRYNYTY health:enablement  |  Andrew@trynytyhealth.com  |  ${new Date().toLocaleDateString('en-US',{month:'long',year:'numeric'})}`, {
    x:0.35, y:5.38, w:9.3, h:0.18,
    fontSize:8, color:"4A6080", fontFace:"Calibri", margin:0 });

  // ── SLIDE 2: Full Savings Rate Trajectory ──────────────────────────────────
  onProgress?.('Building trajectory slide...');
  const s2 = pres.addSlide();
  s2.background = { color: GRAY_50 };

  s2.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:1.05, fill:{ color:NAVY2 } });
  s2.addText("SAVINGS RATE TRAJECTORY", {
    x:0.35, y:0.1, w:9, h:0.25,
    fontSize:10, bold:true, color:TEAL, fontFace:"Calibri", charSpacing:1.5, margin:0 });
  s2.addText(`${aco.aco_name} — Full MSSP History`, {
    x:0.35, y:0.4, w:9, h:0.45,
    fontSize:20, bold:true, color:WHITE, fontFace:"Calibri", margin:0 });

  // Line chart — savings rate over all years
  const chartX = 0.55, chartY = 1.2, chartW = 6.0, chartH = 3.7;
  s2.addShape(pres.shapes.RECTANGLE, { x:0.35, y:1.1, w:6.4, h:3.95,
    fill:{ color:WHITE }, shadow:makeShadow(), line:{ color:"E2E8F0", width:0.5 } });

  const savRates = acoRecords.map(r => r.sav_rate != null ? r.sav_rate * 100 : null);
  const allVals = savRates.filter(v => v != null);
  const minV = Math.min(...allVals, 0) - 1;
  const maxV = Math.max(...allVals, 0) + 2;
  const range = maxV - minV;

  // Grid lines
  [-4,-2,0,2,4,6,8].filter(v => v >= minV && v <= maxV).forEach(v => {
    const yp = chartY + chartH - ((v - minV) / range) * chartH;
    s2.addShape(pres.shapes.LINE, { x:chartX, y:yp, w:chartW, h:0,
      line:{ color:"E2E8F0", width:0.5, dashType: v===0 ? "solid" : "dash" } });
    s2.addShape(pres.shapes.LINE, { x:chartX, y:yp, w:chartW, h:0,
      line:{ color: v===0 ? "94A3B8" : "E2E8F0", width: v===0 ? 1 : 0.5 } });
    s2.addText(`${v}%`, { x:chartX-0.42, y:yp-0.12, w:0.38, h:0.25,
      fontSize:8, color:SLATE, fontFace:"Calibri", align:"right", margin:0 });
  });

  // Plot bars + line
  const nYears = acoRecords.length;
  const barW = Math.min(0.4, chartW / nYears * 0.6);
  const xStep = chartW / Math.max(nYears - 1, 1);

  acoRecords.forEach((r, i) => {
    const xpos = chartX + (nYears > 1 ? i * xStep : chartW/2);
    const sv = r.sav_rate != null ? r.sav_rate * 100 : null;
    if (sv == null) return;
    const yZero = chartY + chartH - ((0 - minV) / range) * chartH;
    const yVal  = chartY + chartH - ((sv - minV) / range) * chartH;
    const barH  = Math.abs(yVal - yZero);
    const barY  = sv >= 0 ? yVal : yZero;
    const col   = sv >= 0 ? TEAL : RED;

    s2.addShape(pres.shapes.RECTANGLE, { x:xpos - barW/2, y:barY, w:barW, h:barH,
      fill:{ color:col }, line:{ color:col } });
    s2.addText(`${sv.toFixed(1)}%`, { x:xpos-0.4, y:sv>=0?yVal-0.28:yZero+0.05, w:0.8, h:0.22,
      fontSize:8, bold:true, color:col, fontFace:"Calibri", align:"center", margin:0 });
    s2.addText(String(r.performance_year), { x:xpos-0.35, y:chartY+chartH+0.04, w:0.7, h:0.2,
      fontSize:8, color:SLATE, fontFace:"Calibri", align:"center", margin:0 });
  });

  // Right summary panel
  const rp = { x:6.95, y:1.1, w:2.7, h:3.95 };
  s2.addShape(pres.shapes.RECTANGLE, { x:rp.x, y:rp.y, w:rp.w, h:rp.h,
    fill:{ color:NAVY2 }, line:{ color:NAVY2 } });

  s2.addText("AT A GLANCE", { x:rp.x+0.2, y:rp.y+0.15, w:rp.w-0.4, h:0.2,
    fontSize:9, bold:true, color:TEAL, charSpacing:1.5, fontFace:"Calibri", margin:0 });

  const summaryStats = [
    { v:`${yearsEarning}/${acoRecords.length}`, l:'Years earning savings' },
    { v:fmtM(totalEarned), l:`Total earned (all years)` },
    { v:fmtSav(avgSavRate), l:'Average savings rate' },
    { v:`${peerRecs.length}`, l:`${latest?.current_track||''} track peers` },
    { v:fmtSav(peerAvg(peerRecs,'sav_rate')), l:`Peer avg PY${latest?.performance_year}` },
  ];
  summaryStats.forEach((s, i) => {
    s2.addShape(pres.shapes.LINE, { x:rp.x+0.2, y:rp.y+0.5+i*0.7, w:rp.w-0.4, h:0,
      line:{ color:"1E3A5A", width:0.5 } });
    s2.addText(s.v, { x:rp.x+0.2, y:rp.y+0.56+i*0.7, w:rp.w-0.4, h:0.35,
      fontSize:20, bold:true, color:TEAL, fontFace:"Calibri", margin:0 });
    s2.addText(s.l, { x:rp.x+0.2, y:rp.y+0.9+i*0.7, w:rp.w-0.4, h:0.2,
      fontSize:9, color:SLATE, fontFace:"Calibri", margin:0 });
  });

  s2.addText(`Source: CMS MSSP PUF PY2013–${latest?.performance_year}  |  Excludes mid-year terminations`, {
    x:0.35, y:5.38, w:9.3, h:0.18,
    fontSize:8, color:SLATE, fontFace:"Calibri", margin:0 });

  // ── SLIDE 3: Benchmark vs Actual — The Gap ─────────────────────────────────
  onProgress?.('Building benchmark slide...');
  const s3 = pres.addSlide();
  s3.background = { color: GRAY_50 };

  s3.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:1.05, fill:{ color:NAVY2 } });
  s3.addText("BENCHMARK VS. ACTUAL EXPENSE", {
    x:0.35, y:0.1, w:9, h:0.25,
    fontSize:10, bold:true, color:TEAL, fontFace:"Calibri", charSpacing:1.5, margin:0 });
  s3.addText("Where the money is — and where it's going", {
    x:0.35, y:0.4, w:9, h:0.45,
    fontSize:20, bold:true, color:WHITE, fontFace:"Calibri", margin:0 });

  // PBPM trend — recent years
  const recentRecs = acoRecords.filter(r => r.benchmark_pbpm != null).slice(-6);

  // Stat cards row
  const cards3 = [
    { v: `$${latest?.benchmark_pbpm?.toFixed(2)||'—'}`, l:'Benchmark PBPM', sub:'Latest year' },
    { v: `$${latest?.expense_pbpm?.toFixed(2)||'—'}`,   l:'Actual Expense PBPM', sub:'Latest year',
      col: (latest?.expense_pbpm||0) < (latest?.benchmark_pbpm||0) ? TEAL : RED },
    { v: latest?.benchmark_pbpm && latest?.expense_pbpm
        ? `$${(latest.benchmark_pbpm - latest.expense_pbpm).toFixed(2)}` : '—',
      l: 'Savings Per Bene / Mo', sub:'Benchmark minus actual',
      col: (latest?.benchmark_pbpm||0) > (latest?.expense_pbpm||0) ? TEAL : RED },
    { v: fmtM(latest?.earn_save_loss), l:`PY${latest?.performance_year} Earned`, sub:'Shared savings payment' },
  ];

  cards3.forEach((card, i) => {
    const cx = 0.35 + i * 2.35;
    s3.addShape(pres.shapes.RECTANGLE, { x:cx, y:1.15, w:2.2, h:1.2,
      fill:{ color:WHITE }, shadow:makeShadow(), line:{ color:"E2E8F0", width:0.5 } });
    s3.addShape(pres.shapes.RECTANGLE, { x:cx, y:1.15, w:0.06, h:1.2,
      fill:{ color:card.col||TEAL }, line:{ color:card.col||TEAL } });
    s3.addText(card.v, { x:cx+0.15, y:1.22, w:2.0, h:0.52,
      fontSize:24, bold:true, color:card.col||TEAL, fontFace:"Calibri", margin:0 });
    s3.addText(card.l, { x:cx+0.15, y:1.75, w:2.0, h:0.22,
      fontSize:10, bold:true, color:NAVY2, fontFace:"Calibri", margin:0 });
    s3.addText(card.sub, { x:cx+0.15, y:1.96, w:2.0, h:0.18,
      fontSize:9, color:SLATE, fontFace:"Calibri", margin:0 });
  });

  // PBPM trend chart
  s3.addShape(pres.shapes.RECTANGLE, { x:0.35, y:2.52, w:9.3, h:2.6,
    fill:{ color:WHITE }, shadow:makeShadow(), line:{ color:"E2E8F0", width:0.5 } });
  s3.addText("Benchmark vs. Expense PBPM — Recent Years", {
    x:0.55, y:2.6, w:8, h:0.25,
    fontSize:11, bold:true, color:NAVY2, fontFace:"Calibri", margin:0 });

  if (recentRecs.length > 1) {
    const cX = 0.75, cY = 2.95, cW = 8.7, cH = 1.9;
    const allPBPM = recentRecs.flatMap(r => [r.benchmark_pbpm, r.expense_pbpm]).filter(v=>v!=null);
    const pbMin = Math.min(...allPBPM) * 0.95;
    const pbMax = Math.max(...allPBPM) * 1.05;
    const pbRange = pbMax - pbMin;
    const pbStep = cW / (recentRecs.length - 1);

    // Draw lines
    const datasets = [
      { key:'benchmark_pbpm', color:NAVY2, label:'Benchmark' },
      { key:'expense_pbpm',   color:RED,   label:'Actual Expense' },
    ];
    datasets.forEach(ds => {
      for (let i = 0; i < recentRecs.length - 1; i++) {
        const r1 = recentRecs[i], r2 = recentRecs[i+1];
        const v1 = r1[ds.key], v2 = r2[ds.key];
        if (v1==null||v2==null) continue;
        const x1 = cX + i*pbStep;
        const y1 = cY + cH - ((v1-pbMin)/pbRange)*cH;
        const x2 = cX + (i+1)*pbStep;
        const y2 = cY + cH - ((v2-pbMin)/pbRange)*cH;
        s3.addShape(pres.shapes.LINE, { x:x1, y:y1, w:x2-x1, h:y2-y1,
          line:{ color:ds.color, width:2.5 } });
      }
      recentRecs.forEach((r, i) => {
        const v = r[ds.key];
        if (v==null) return;
        const dx = cX + i*pbStep;
        const dy = cY + cH - ((v-pbMin)/pbRange)*cH;
        s3.addShape(pres.shapes.OVAL, { x:dx-0.08, y:dy-0.08, w:0.16, h:0.16,
          fill:{ color:ds.color }, line:{ color:ds.color } });
      });
    });

    // X labels
    recentRecs.forEach((r, i) => {
      s3.addText(String(r.performance_year), { x:cX+i*pbStep-0.3, y:cY+cH+0.04, w:0.6, h:0.2,
        fontSize:8, color:SLATE, fontFace:"Calibri", align:"center", margin:0 });
    });

    // Legend
    [{ c:NAVY2, l:'Benchmark PBPM' }, { c:RED, l:'Actual Expense PBPM' }].forEach((lg,i)=>{
      s3.addShape(pres.shapes.LINE, { x:0.75+i*2.2, y:5.1, w:0.3, h:0, line:{ color:lg.c, width:2.5 } });
      s3.addText(lg.l, { x:1.15+i*2.2, y:5.01, w:2, h:0.2, fontSize:9, color:NAVY2, fontFace:"Calibri", margin:0 });
    });
  }

  s3.addText(`Source: CMS MSSP PUF  |  PBPM = per beneficiary per month  |  Earned = gross savings × sharing rate`, {
    x:0.35, y:5.38, w:9.3, h:0.18,
    fontSize:8, color:SLATE, fontFace:"Calibri", margin:0 });

  // ── SLIDE 4: Peer Scorecard ────────────────────────────────────────────────
  onProgress?.('Building peer scorecard...');
  const s4 = pres.addSlide();
  s4.background = { color: GRAY_50 };

  s4.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:1.05, fill:{ color:NAVY2 } });
  s4.addText("PEER SCORECARD", {
    x:0.35, y:0.1, w:9, h:0.25,
    fontSize:10, bold:true, color:TEAL, fontFace:"Calibri", charSpacing:1.5, margin:0 });
  s4.addText(`vs ${peerRecs.length} ${latest?.current_track||''} Track Peers — PY${latest?.performance_year}`, {
    x:0.35, y:0.4, w:9, h:0.45,
    fontSize:20, bold:true, color:WHITE, fontFace:"Calibri", margin:0 });

  const metrics4 = [
    { l:'Savings Rate', v:latest?.sav_rate, peer:peerAvg(peerRecs,'sav_rate'),
      fmt:v=>fmtSav(v), pctFn:()=>peerPctSav(peerRecs,latest?.sav_rate), lowerBetter:false },
    { l:'Quality Score', v:latest?.qual_score, peer:peerAvg(peerRecs,'qual_score'),
      fmt:v=>`${v.toFixed(1)}`, pctFn:()=>peerPct(peerRecs,'qual_score',latest?.qual_score), lowerBetter:false },
    { l:'Expense PBPM', v:latest?.expense_pbpm, peer:peerAvg(peerRecs,'expense_pbpm'),
      fmt:v=>`$${v.toFixed(2)}`, pctFn:()=>peerPct(peerRecs,'expense_pbpm',latest?.expense_pbpm), lowerBetter:true },
    { l:'SNF Admits/1K', v:latest?.p_snf_adm, peer:peerAvg(peerRecs,'p_snf_adm'),
      fmt:v=>`${v.toFixed(1)}`, pctFn:()=>peerPct(peerRecs,'p_snf_adm',latest?.p_snf_adm), lowerBetter:true },
    { l:'ED Visits/1K', v:latest?.p_edv_vis, peer:peerAvg(peerRecs,'p_edv_vis'),
      fmt:v=>`${v.toFixed(1)}`, pctFn:()=>peerPct(peerRecs,'p_edv_vis',latest?.p_edv_vis), lowerBetter:true },
    { l:'PCP E&M/1K', v:latest?.p_em_pcp_vis, peer:peerAvg(peerRecs,'p_em_pcp_vis'),
      fmt:v=>`${v.toFixed(0)}`, pctFn:()=>peerPct(peerRecs,'p_em_pcp_vis',latest?.p_em_pcp_vis), lowerBetter:false },
  ];

  // Horizontal bar comparison chart
  metrics4.forEach((m, i) => {
    const my = 1.2 + i * 0.7;
    const pct = m.pctFn();
    const better = m.v!=null && m.peer!=null ? (m.lowerBetter?m.v<m.peer:m.v>m.peer) : null;
    const col = better===true ? TEAL : better===false ? RED : SLATE;

    s4.addShape(pres.shapes.RECTANGLE, { x:0.35, y:my, w:9.3, h:0.65,
      fill:{ color: i%2===0?"F8FAFC":WHITE }, line:{ color:"E2E8F0", width:0.3 } });

    s4.addText(m.l, { x:0.5, y:my+0.18, w:1.6, h:0.28,
      fontSize:11, color:NAVY2, fontFace:"Calibri", margin:0 });
    s4.addText(m.v!=null ? m.fmt(m.v) : '—', { x:2.2, y:my+0.13, w:1.4, h:0.38,
      fontSize:20, bold:true, color:col, fontFace:"Calibri", margin:0 });

    // Progress bar
    if (pct != null) {
      s4.addShape(pres.shapes.RECTANGLE, { x:3.75, y:my+0.2, w:4.5, h:0.24,
        fill:{ color:"E2E8F0" }, line:{ color:"E2E8F0" } });
      s4.addShape(pres.shapes.RECTANGLE, { x:3.75, y:my+0.2, w:4.5*(pct/100), h:0.24,
        fill:{ color:col }, line:{ color:col } });
      s4.addText(`${pct}th pct`, { x:8.35, y:my+0.18, w:1.1, h:0.28,
        fontSize:11, bold:true, color:col, fontFace:"Calibri", align:"right", margin:0 });
    }

    if (m.peer!=null) {
      s4.addText(`Peer avg: ${m.fmt(m.peer)}`, { x:3.75, y:my+0.46, w:3, h:0.18,
        fontSize:8, color:SLATE, fontFace:"Calibri", margin:0 });
    }
  });

  // Insight at bottom
  const savPct4 = peerPctSav(peerRecs, latest?.sav_rate);
  const snfPct4 = peerPct(peerRecs, 'p_snf_adm', latest?.p_snf_adm);
  let insight4 = '';
  if (savPct4 != null && snfPct4 != null) {
    if (snfPct4 > 65)
      insight4 = `Strong savings rate (${savPct4}th pct) — but SNF utilization above ${snfPct4}% of peers signals the largest near-term cost opportunity.`;
    else if (savPct4 > 65)
      insight4 = `Top-tier savings performance at the ${savPct4}th percentile with well-controlled utilization across key drivers.`;
    else
      insight4 = `Savings rate at the ${savPct4}th percentile. Focus on SNF and ED utilization management to close the peer gap.`;
  }
  if (insight4) {
    s4.addShape(pres.shapes.RECTANGLE, { x:0.35, y:5.12, w:9.3, h:0.3,
      fill:{ color:NAVY2 }, line:{ color:NAVY2 } });
    s4.addText(`💡  ${insight4}`, { x:0.5, y:5.14, w:9.0, h:0.26,
      fontSize:9, color:TEAL, fontFace:"Calibri", margin:0 });
  }

  s4.addText(`Source: CMS MSSP PUF PY${latest?.performance_year}  |  Peer group: ${peerRecs.length} ${latest?.current_track||''} track ACOs (non-midyear)`, {
    x:0.35, y:5.38, w:9.3, h:0.18,
    fontSize:8, color:SLATE, fontFace:"Calibri", margin:0 });

  // ── SLIDE 5: Quality Deep Dive ─────────────────────────────────────────────
  onProgress?.('Building quality slide...');
  const s5 = pres.addSlide();
  s5.background = { color: GRAY_50 };

  s5.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:1.05, fill:{ color:NAVY2 } });
  s5.addText("QUALITY PERFORMANCE DEEP DIVE", {
    x:0.35, y:0.1, w:9, h:0.25,
    fontSize:10, bold:true, color:TEAL, fontFace:"Calibri", charSpacing:1.5, margin:0 });
  s5.addText(`${aco.aco_name} — Quality Score Trend & CAHPS Patient Experience`, {
    x:0.35, y:0.4, w:9, h:0.45,
    fontSize:18, bold:true, color:WHITE, fontFace:"Calibri", margin:0 });

  // Quality score trend (recent 6 years)
  const qualRecs = acoRecords.filter(r => r.qual_score != null).slice(-7);
  s5.addShape(pres.shapes.RECTANGLE, { x:0.35, y:1.12, w:5.8, h:2.4,
    fill:{ color:WHITE }, shadow:makeShadow(), line:{ color:"E2E8F0", width:0.5 } });
  s5.addText("Quality Score Trend", { x:0.55, y:1.18, w:5, h:0.22,
    fontSize:11, bold:true, color:NAVY2, fontFace:"Calibri", margin:0 });

  if (qualRecs.length > 0) {
    const qX = 0.65, qY = 1.48, qW = 5.4, qH = 1.8;
    const qMax = 105, qMin = Math.max(0, Math.min(...qualRecs.map(r=>r.qual_score))-10);
    const qRange = qMax - qMin;
    const qStep = qW / Math.max(qualRecs.length-1, 1);

    [60,70,80,90,100].filter(v=>v>=qMin&&v<=qMax).forEach(v=>{
      const yp = qY + qH - ((v-qMin)/qRange)*qH;
      s5.addShape(pres.shapes.LINE, { x:qX, y:yp, w:qW, h:0,
        line:{ color:"E2E8F0", width:0.5, dashType:"dash" } });
      s5.addText(`${v}`, { x:qX-0.38, y:yp-0.1, w:0.34, h:0.2,
        fontSize:8, color:SLATE, fontFace:"Calibri", align:"right", margin:0 });
    });

    for (let i=0; i<qualRecs.length-1; i++) {
      const r1=qualRecs[i], r2=qualRecs[i+1];
      const x1=qX+i*qStep, y1=qY+qH-((r1.qual_score-qMin)/qRange)*qH;
      const x2=qX+(i+1)*qStep, y2=qY+qH-((r2.qual_score-qMin)/qRange)*qH;
      s5.addShape(pres.shapes.LINE, { x:x1,y:y1,w:x2-x1,h:y2-y1,
        line:{ color:TEAL, width:2.5 } });
    }
    qualRecs.forEach((r,i)=>{
      const dx=qX+i*qStep, dy=qY+qH-((r.qual_score-qMin)/qRange)*qH;
      s5.addShape(pres.shapes.OVAL,{ x:dx-0.09,y:dy-0.09,w:0.18,h:0.18,
        fill:{ color:TEAL }, line:{ color:TEAL } });
      s5.addText(r.qual_score.toFixed(0),{ x:dx-0.3,y:dy-0.3,w:0.6,h:0.22,
        fontSize:8,bold:true,color:TEAL,fontFace:"Calibri",align:"center",margin:0 });
      s5.addText(String(r.performance_year),{ x:dx-0.3,y:qY+qH+0.04,w:0.6,h:0.2,
        fontSize:8,color:SLATE,fontFace:"Calibri",align:"center",margin:0 });
    });
  }

  // CAHPS latest year
  const cahpsLabels = {
    cahps_1:'Getting Needed Care', cahps_2:'Getting Care Quickly',
    cahps_3:'Doctor Communication', cahps_4:"Patient Rating of Doctor",
    cahps_5:'Access to Specialists', cahps_9:'Care Coordination',
  };
  const cahpsRec = [...acoRecords].reverse().find(r => r.cahps_1 != null);

  s5.addShape(pres.shapes.RECTANGLE, { x:6.35, y:1.12, w:3.3, h:2.4,
    fill:{ color:WHITE }, shadow:makeShadow(), line:{ color:"E2E8F0", width:0.5 } });
  s5.addText(`CAHPS Patient Experience — PY${cahpsRec?.performance_year||'N/A'}`, {
    x:6.55, y:1.18, w:3.0, h:0.22,
    fontSize:11, bold:true, color:NAVY2, fontFace:"Calibri", margin:0 });

  if (cahpsRec) {
    Object.entries(cahpsLabels).forEach(([key, label], i) => {
      const val = cahpsRec[key];
      if (val == null) return;
      const cy2 = 1.45 + i * 0.32;
      s5.addText(label, { x:6.55, y:cy2, w:2.1, h:0.22,
        fontSize:9, color:NAVY2, fontFace:"Calibri", margin:0 });
      s5.addShape(pres.shapes.RECTANGLE, { x:8.7, y:cy2+0.04, w:0.65, h:0.16,
        fill:{ color:"E2E8F0" }, line:{ color:"E2E8F0" } });
      s5.addShape(pres.shapes.RECTANGLE, { x:8.7, y:cy2+0.04, w:0.65*(val/100), h:0.16,
        fill:{ color:TEAL }, line:{ color:TEAL } });
      s5.addText(`${val.toFixed(0)}`, { x:9.38, y:cy2, w:0.28, h:0.22,
        fontSize:9, bold:true, color:TEAL, fontFace:"Calibri", margin:0 });
    });
  } else {
    s5.addText("CAHPS data available from PY2021 onward.", {
      x:6.55, y:1.5, w:3.0, h:0.4,
      fontSize:10, color:SLATE, fontFace:"Calibri", margin:0 });
  }

  // Clinical measures table (latest year)
  s5.addShape(pres.shapes.RECTANGLE, { x:0.35, y:3.65, w:9.3, h:1.72,
    fill:{ color:WHITE }, shadow:makeShadow(), line:{ color:"E2E8F0", width:0.5 } });
  s5.addText(`Clinical Quality Measures — PY${latest?.performance_year}`, {
    x:0.55, y:3.72, w:6, h:0.22,
    fontSize:11, bold:true, color:NAVY2, fontFace:"Calibri", margin:0 });

  const clinicalKeys = ['qualityid_318','qualityid_110','qualityid_226','qualityid_113','qualityid_112','qualityid_438','measure_479'];
  const clinLabels = {
    qualityid_318:'Fall Risk Screening', qualityid_110:'Influenza Immunization',
    qualityid_226:'Tobacco Cessation', qualityid_113:'Colorectal Cancer Screening',
    qualityid_112:'Breast Cancer Screening', qualityid_438:'Statin Therapy (CV)',
    measure_479:'30-Day Readmission Rate',
  };
  const clinColW = 1.26;
  clinicalKeys.slice(0,7).forEach((key,i) => {
    const val = latest?.[key];
    const cx3 = 0.5 + i*clinColW;
    s5.addText(clinLabels[key], { x:cx3, y:4.0, w:clinColW-0.05, h:0.45,
      fontSize:8, color:SLATE, fontFace:"Calibri", align:"center", margin:0 });
    s5.addText(val!=null?`${Number(val).toFixed(1)}%`:'N/A', {
      x:cx3, y:4.45, w:clinColW-0.05, h:0.38,
      fontSize:15, bold:true,
      color:val!=null?(key==='measure_479'?val<15?TEAL:RED:val>70?TEAL:SLATE):SLATE,
      fontFace:"Calibri", align:"center", margin:0 });
  });

  s5.addText(`Source: CMS MSSP PUF  |  CAHPS: 0–100 scale, higher = better  |  Readmission: lower = better`, {
    x:0.35, y:5.38, w:9.3, h:0.18,
    fontSize:8, color:SLATE, fontFace:"Calibri", margin:0 });

  // ── SLIDE 6: Utilization Profile ───────────────────────────────────────────
  onProgress?.('Building utilization slide...');
  const s6 = pres.addSlide();
  s6.background = { color: GRAY_50 };

  s6.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:10, h:1.05, fill:{ color:NAVY2 } });
  s6.addText("UTILIZATION PROFILE & COST DRIVERS", {
    x:0.35, y:0.1, w:9, h:0.25,
    fontSize:10, bold:true, color:TEAL, fontFace:"Calibri", charSpacing:1.5, margin:0 });
  s6.addText("Where your cost story lives — and what to target", {
    x:0.35, y:0.4, w:9, h:0.45,
    fontSize:20, bold:true, color:WHITE, fontFace:"Calibri", margin:0 });

  // Spend category table
  const spendCats = [
    { l:'Inpatient (All)', key:'capann_inp_all', icon:'🏥' },
    { l:'SNF',             key:'capann_snf',     icon:'🛏' },
    { l:'Outpatient',      key:'capann_opd',     icon:'🩺' },
    { l:'Physician / PB',  key:'capann_pb',      icon:'👨‍⚕️' },
    { l:'Home Health',     key:'capann_hha',     icon:'🏠' },
    { l:'Hospice',         key:'capann_hsp',     icon:'💊' },
    { l:'Ambulance',       key:'capann_ambpay',  icon:'🚑' },
    { l:'DME',             key:'capann_dme',     icon:'🦽' },
  ];

  s6.addShape(pres.shapes.RECTANGLE, { x:0.35, y:1.12, w:5.8, h:4.15,
    fill:{ color:WHITE }, shadow:makeShadow(), line:{ color:"E2E8F0", width:0.5 } });
  s6.addText("Per Capita Annual Spend by Category", {
    x:0.55, y:1.18, w:5, h:0.22,
    fontSize:11, bold:true, color:NAVY2, fontFace:"Calibri", margin:0 });

  const maxSpend = Math.max(...spendCats.map(c => latest?.[c.key]||0));
  spendCats.forEach((cat, i) => {
    const val = latest?.[cat.key];
    const sy = 1.5 + i * 0.46;
    s6.addText(`${cat.icon} ${cat.l}`, { x:0.5, y:sy+0.08, w:1.8, h:0.28,
      fontSize:10, color:NAVY2, fontFace:"Calibri", margin:0 });
    // Bar
    const barLen = val && maxSpend > 0 ? 3.0 * (val/maxSpend) : 0;
    s6.addShape(pres.shapes.RECTANGLE, { x:2.4, y:sy+0.1, w:3.0, h:0.22,
      fill:{ color:"E2E8F0" }, line:{ color:"E2E8F0" } });
    if (barLen > 0) {
      s6.addShape(pres.shapes.RECTANGLE, { x:2.4, y:sy+0.1, w:barLen, h:0.22,
        fill:{ color: i<2?NAVY2:TEAL }, line:{ color: i<2?NAVY2:TEAL } });
    }
    s6.addText(val ? `$${val.toLocaleString()}` : '—', { x:5.5, y:sy+0.08, w:0.6, h:0.28,
      fontSize:10, bold:true, color:NAVY2, fontFace:"Calibri", align:"right", margin:0 });
  });

  // Utilization metrics panel
  s6.addShape(pres.shapes.RECTANGLE, { x:6.35, y:1.12, w:3.3, h:4.15,
    fill:{ color:NAVY2 }, line:{ color:NAVY2 } });
  s6.addText("UTILIZATION RATES / 1K", {
    x:6.55, y:1.2, w:3.0, h:0.22,
    fontSize:9, bold:true, color:TEAL, charSpacing:1.5, fontFace:"Calibri", margin:0 });

  const utilMetrics = [
    { l:'Total Admissions', v:latest?.adm, peer:peerAvg(peerRecs,'adm'), lower:true },
    { l:'ED Visits/1K', v:latest?.p_edv_vis, peer:peerAvg(peerRecs,'p_edv_vis'), lower:true },
    { l:'ED → Admits/1K', v:latest?.p_edv_vis_hosp, peer:peerAvg(peerRecs,'p_edv_vis_hosp'), lower:true },
    { l:'SNF Admits/1K', v:latest?.p_snf_adm, peer:peerAvg(peerRecs,'p_snf_adm'), lower:true },
    { l:'PCP E&M/1K', v:latest?.p_em_pcp_vis, peer:peerAvg(peerRecs,'p_em_pcp_vis'), lower:false },
    { l:'Specialist E&M/1K', v:latest?.p_em_sp_vis, peer:peerAvg(peerRecs,'p_em_sp_vis'), lower:true },
  ];

  utilMetrics.forEach((m, i) => {
    const better = m.v!=null && m.peer!=null ? (m.lower?m.v<m.peer:m.v>m.peer) : null;
    const col = better===true?TEAL:better===false?RED:SLATE_L;
    const uy = 1.52 + i * 0.6;
    s6.addShape(pres.shapes.LINE, { x:6.55, y:uy, w:2.9, h:0,
      line:{ color:"1E3A5A", width:0.5 } });
    s6.addText(m.l, { x:6.55, y:uy+0.04, w:2.9, h:0.2,
      fontSize:9, color:SLATE, fontFace:"Calibri", margin:0 });
    s6.addText(m.v!=null?m.v.toFixed(1):'—', { x:6.55, y:uy+0.24, w:1.4, h:0.28,
      fontSize:17, bold:true, color:col, fontFace:"Calibri", margin:0 });
    if (m.peer!=null) {
      s6.addText(`peer: ${m.peer.toFixed(1)}`, { x:8.05, y:uy+0.28, w:0.9, h:0.22,
        fontSize:9, color:"4A6080", fontFace:"Calibri", align:"right", margin:0 });
    }
  });

  s6.addText(`Source: CMS MSSP PUF PY${latest?.performance_year}  |  Per capita spend = cap_ann fields  |  Utilization = per 1,000 person-years`, {
    x:0.35, y:5.38, w:9.3, h:0.18,
    fontSize:8, color:SLATE, fontFace:"Calibri", margin:0 });

  // ── SLIDE 7: Track Analysis & What-If ─────────────────────────────────────
  onProgress?.('Building track analysis slide...');
  const s7 = pres.addSlide();
  s7.background = { color: NAVY };
  s7.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:0.08, h:5.625, fill:{ color:TEAL } });

  s7.addText("TRACK & FINANCIAL STRUCTURE", {
    x:0.35, y:0.18, w:9.3, h:0.25,
    fontSize:10, bold:true, color:TEAL, fontFace:"Calibri", charSpacing:1.5, margin:0 });
  s7.addText("Current configuration and what the numbers mean", {
    x:0.35, y:0.45, w:9.3, h:0.45,
    fontSize:22, bold:true, color:WHITE, fontFace:"Calibri", margin:0 });

  // Current track config card
  s7.addShape(pres.shapes.RECTANGLE, { x:0.35, y:1.05, w:4.5, h:3.55,
    fill:{ color:"0A1929" }, line:{ color:TEAL, width:1 } });
  s7.addText("CURRENT CONFIGURATION", {
    x:0.55, y:1.12, w:4.1, h:0.22,
    fontSize:9, bold:true, color:TEAL, charSpacing:1.5, fontFace:"Calibri", margin:0 });

  const configRows = [
    { l:'Track', v:latest?.current_track || '—' },
    { l:'Risk Model', v:latest?.risk_model || '—' },
    { l:'Agreement Year', v:latest?.performance_year?.toString() || '—' },
    { l:'Assigned Benes', v:latest?.n_ab?.toLocaleString() || '—' },
    { l:'Sharing Rate', v:latest?.final_share_rate != null ? `${(latest.final_share_rate*100).toFixed(0)}%` : '—' },
    { l:'Generated Savings', v:fmtM(latest?.gen_save_loss) },
    { l:'Earned Savings', v:fmtM(latest?.earn_save_loss),
      col: (latest?.earn_save_loss||0)>0?TEAL:RED },
  ];

  configRows.forEach((row, i) => {
    s7.addShape(pres.shapes.LINE, { x:0.55, y:1.42+i*0.32, w:4.1, h:0,
      line:{ color:"1E3A5A", width:0.5 } });
    s7.addText(row.l, { x:0.55, y:1.45+i*0.32, w:2.0, h:0.28,
      fontSize:11, color:SLATE, fontFace:"Calibri", margin:0 });
    s7.addText(row.v, { x:2.65, y:1.45+i*0.32, w:2.0, h:0.28,
      fontSize:11, bold:true, color:row.col||WHITE, fontFace:"Calibri", margin:0 });
  });

  // Track history timeline (right side)
  s7.addShape(pres.shapes.RECTANGLE, { x:5.1, y:1.05, w:4.55, h:3.55,
    fill:{ color:"0A1929" }, line:{ color:"1E3A5A", width:0.5 } });
  s7.addText("TRACK HISTORY", {
    x:5.3, y:1.12, w:4.1, h:0.22,
    fontSize:9, bold:true, color:TEAL, charSpacing:1.5, fontFace:"Calibri", margin:0 });

  // Group by track
  const byTrack = {};
  acoRecords.forEach(r => {
    const t = r.current_track || 'Unknown';
    if (!byTrack[t]) byTrack[t] = [];
    byTrack[t].push(r.performance_year);
  });

  Object.entries(byTrack).forEach(([track, years], i) => {
    const trackRecs = acoRecords.filter(r => r.current_track === track);
    const avgSav = avg(trackRecs, r => r.sav_rate);
    const earned = trackRecs.reduce((s,r)=>s+(r.earn_save_loss||0),0);
    const ty = 1.45 + i * 0.85;

    s7.addShape(pres.shapes.RECTANGLE, { x:5.2, y:ty, w:4.25, h:0.75,
      fill:{ color:NAVY2 }, line:{ color:"1E3A5A", width:0.3 } });
    s7.addText(track, { x:5.35, y:ty+0.06, w:3.5, h:0.28,
      fontSize:13, bold:true, color:WHITE, fontFace:"Calibri", margin:0 });
    s7.addText(`${years.length} year${years.length!==1?'s':''}: ${Math.min(...years)}–${Math.max(...years)}`, {
      x:5.35, y:ty+0.35, w:2.2, h:0.22,
      fontSize:10, color:SLATE, fontFace:"Calibri", margin:0 });
    s7.addText(`Avg: ${fmtSav(avgSav)}`, { x:7.55, y:ty+0.06, w:1.7, h:0.28,
      fontSize:12, bold:true, color:(avgSav||0)>0?TEAL:RED, fontFace:"Calibri", align:"right", margin:0 });
    s7.addText(`Earned: ${fmtM(earned)}`, { x:7.55, y:ty+0.35, w:1.7, h:0.22,
      fontSize:10, color:(earned||0)>0?TEAL:RED, fontFace:"Calibri", align:"right", margin:0 });
  });

  s7.addText(`Application deadline: May 17, 2026 for LEAD Model (MSSP successor)  |  trynytyhealth.com/tools`, {
    x:0.35, y:4.75, w:9.3, h:0.22,
    fontSize:9, color:GOLD, fontFace:"Calibri", margin:0 });

  // ── SLIDE 8: The Ask ────────────────────────────────────────────────────────
  onProgress?.('Finalizing...');
  const s8 = pres.addSlide();
  s8.background = { color: NAVY };
  s8.addShape(pres.shapes.RECTANGLE, { x:0, y:0, w:0.08, h:5.625, fill:{ color:TEAL } });

  s8.addText("TRYNYTY", { x:0.35, y:0.28, w:4, h:0.42,
    fontSize:24, bold:true, color:WHITE, fontFace:"Calibri", charSpacing:3, margin:0 });
  s8.addText("health:enablement", { x:0.35, y:0.68, w:4, h:0.25,
    fontSize:12, color:TEAL, fontFace:"Calibri", margin:0 });

  // Personalized hook
  s8.addText(`"${finding}"`, {
    x:0.35, y:1.1, w:5.2, h:1.1,
    fontSize:14, color:SLATE_L, fontFace:"Calibri", italic:true, margin:0 });

  s8.addText("This is the kind of analysis we bring to every engagement.", {
    x:0.35, y:2.28, w:5.2, h:0.38,
    fontSize:14, bold:true, color:WHITE, fontFace:"Calibri", margin:0 });

  s8.addText("HOW WE CAN HELP", { x:0.35, y:2.82, w:5, h:0.22,
    fontSize:9, bold:true, color:TEAL, charSpacing:1.5, fontFace:"Calibri", margin:0 });
  [
    "MSSP performance benchmarking & strategy",
    "Financial modeling & savings projection",
    "Track upgrade & configuration analysis",
    "Quality measure improvement roadmap",
    "LEAD Model transition planning",
    "Custom analytics & performance dashboards",
  ].forEach((svc, i) => {
    s8.addShape(pres.shapes.RECTANGLE, { x:0.35, y:3.1+i*0.36, w:0.06, h:0.06,
      fill:{ color:TEAL }, line:{ color:TEAL } });
    s8.addText(svc, { x:0.5, y:3.03+i*0.36, w:4.7, h:0.3,
      fontSize:11, color:SLATE_L, fontFace:"Calibri", margin:0 });
  });

  // CTA box
  s8.addShape(pres.shapes.RECTANGLE, { x:5.8, y:0.28, w:3.85, h:5.05,
    fill:{ color:"0A1929" }, line:{ color:TEAL, width:1 } });
  s8.addText("Let's talk.", { x:6.0, y:0.52, w:3.45, h:0.48,
    fontSize:26, bold:true, color:WHITE, fontFace:"Calibri", margin:0 });
  s8.addText(`We've already done the analysis on ${aco.aco_name}. A 30-minute conversation with Andrew Smithson can turn this data into a concrete strategy.`, {
    x:6.0, y:1.1, w:3.45, h:1.0,
    fontSize:11, color:SLATE_L, fontFace:"Calibri", margin:0 });

  s8.addShape(pres.shapes.RECTANGLE, { x:6.0, y:2.28, w:3.45, h:0.52,
    fill:{ color:TEAL }, line:{ color:TEAL } });
  s8.addText("Schedule a Conversation →", { x:6.0, y:2.28, w:3.45, h:0.52,
    fontSize:13, bold:true, color:NAVY, fontFace:"Calibri", align:"center", valign:"middle", margin:0 });
  s8.addText("trynytyhealth.com/contact", { x:6.0, y:2.88, w:3.45, h:0.25,
    fontSize:11, color:TEAL, fontFace:"Calibri", align:"center", margin:0 });

  s8.addShape(pres.shapes.LINE, { x:6.0, y:3.28, w:3.45, h:0,
    line:{ color:"1E3A5A", width:0.5 } });

  s8.addText("Or explore your ACO's data:", { x:6.0, y:3.42, w:3.45, h:0.25,
    fontSize:11, color:SLATE, fontFace:"Calibri", margin:0 });
  s8.addShape(pres.shapes.RECTANGLE, { x:6.0, y:3.75, w:3.45, h:0.48,
    fill:{ color:"1E3A5A" }, line:{ color:TEAL, width:0.5 } });
  s8.addText("Launch MSSP ACO Explorer →", { x:6.0, y:3.75, w:3.45, h:0.48,
    fontSize:11, bold:true, color:TEAL, fontFace:"Calibri", align:"center", valign:"middle", margin:0 });
  s8.addText("mssppuf.trynytyhealth.com", { x:6.0, y:4.3, w:3.45, h:0.22,
    fontSize:10, color:SLATE, fontFace:"Calibri", align:"center", margin:0 });
  s8.addText("Andrew@trynytyhealth.com  |  Chicago, IL", {
    x:6.0, y:4.68, w:3.45, h:0.22,
    fontSize:9, color:"4A6080", fontFace:"Calibri", align:"center", margin:0 });

  s8.addShape(pres.shapes.RECTANGLE, { x:0, y:5.4, w:10, h:0.225,
    fill:{ color:"061020" }, line:{ color:"061020" } });
  s8.addText("© 2026 TRYNYTY health:enablement  |  Analysis based on CMS MSSP Public Use Files PY2013–2024  |  Free for educational use", {
    x:0.35, y:5.42, w:9.3, h:0.18,
    fontSize:7, color:"4A6080", fontFace:"Calibri", margin:0 });

  // ── Write file ──────────────────────────────────────────────────────────────
  onProgress?.('Generating file...');
  const safeName = aco.aco_name.replace(/[^a-z0-9]/gi, '_').slice(0, 40);
  await pres.writeFile({ fileName: `TRYNYTY_MSSP_Report_${safeName}.pptx` });
  onProgress?.(null);
}
